<script setup>
// Documentos da base de conhecimento da IA (ver AI.md).
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import AiPageLayout from '../components/AiPageLayout.vue';
import aiAPI from 'dashboard/api/ai';
import { useAiInboxSettings } from '../composables/useAiInboxSettings';

const POLL_INTERVAL = 3000;
const ACCEPT = '.pdf,.docx,.txt,.md,.markdown';

const { t } = useI18n();
const { providerConfigured, fetchSettings } = useAiInboxSettings();

const documents = ref([]);
const isLoading = ref(true);
const isUploading = ref(false);
const fileInput = ref(null);
const deleteDialog = ref(null);
const documentToDelete = ref(null);
const busyIds = ref([]);
let pollTimer = null;

const hasProcessing = computed(() =>
  documents.value.some(document => document.status === 'processing')
);

// PortVia: "Pronto" na cor da marca (escala blue gerada pelo branding.yml).
const statusClasses = {
  processing: 'bg-n-amber-3 text-n-amber-11',
  ready: 'bg-n-blue-3 text-n-blue-11',
  failed: 'bg-n-ruby-3 text-n-ruby-11',
};

const formatSize = bytes => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = timestamp =>
  new Date(timestamp * 1000).toLocaleString('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });

const statusLabels = computed(() => ({
  processing: t('AI.DOCUMENTS.STATUS.PROCESSING'),
  ready: t('AI.DOCUMENTS.STATUS.READY'),
  failed: t('AI.DOCUMENTS.STATUS.FAILED'),
}));

const documentMeta = document =>
  [
    document.file_name,
    formatSize(document.file_size),
    document.status === 'ready'
      ? t('AI.DOCUMENTS.CHUNKS', { count: document.chunks_count })
      : null,
    t('AI.DOCUMENTS.UPLOADED_AT', { date: formatDate(document.created_at) }),
  ]
    .filter(Boolean)
    .join(' · ');

const fetchDocuments = async () => {
  try {
    const { data } = await aiAPI.getDocuments();
    documents.value = data.payload;
  } catch {
    useAlert(t('AI.DOCUMENTS.API.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const openFilePicker = () => fileInput.value?.click();

const onFileSelected = async event => {
  const [file] = event.target.files;
  event.target.value = '';
  if (!file) return;

  isUploading.value = true;
  try {
    const { data } = await aiAPI.uploadDocument(file);
    documents.value = [data, ...documents.value];
    useAlert(t('AI.DOCUMENTS.API.UPLOAD_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('AI.DOCUMENTS.API.UPLOAD_ERROR')
    );
  } finally {
    isUploading.value = false;
  }
};

const withBusy = async (id, action) => {
  busyIds.value = [...busyIds.value, id];
  try {
    await action();
  } catch {
    useAlert(t('AI.DOCUMENTS.API.ERROR'));
  } finally {
    busyIds.value = busyIds.value.filter(busyId => busyId !== id);
  }
};

const reprocess = document =>
  withBusy(document.id, async () => {
    const { data } = await aiAPI.reprocessDocument(document.id);
    documents.value = documents.value.map(item =>
      item.id === data.id ? data : item
    );
    useAlert(t('AI.DOCUMENTS.API.REPROCESS_SUCCESS'));
  });

const askDelete = document => {
  documentToDelete.value = document;
  deleteDialog.value?.open();
};

const confirmDelete = () => {
  const document = documentToDelete.value;
  deleteDialog.value?.close();
  if (!document) return;
  withBusy(document.id, async () => {
    await aiAPI.deleteDocument(document.id);
    documents.value = documents.value.filter(item => item.id !== document.id);
    useAlert(t('AI.DOCUMENTS.API.DELETE_SUCCESS'));
  });
};

onMounted(() => {
  fetchDocuments();
  fetchSettings();
});
// Atualiza a lista enquanto houver documento processando.
watch(hasProcessing, processing => {
  clearInterval(pollTimer);
  pollTimer = processing ? setInterval(fetchDocuments, POLL_INTERVAL) : null;
});

onBeforeUnmount(() => clearInterval(pollTimer));
</script>

<template>
  <AiPageLayout
    :title="t('AI.DOCUMENTS.TITLE')"
    :description="t('AI.DOCUMENTS.DESCRIPTION')"
    :provider-warning="!providerConfigured"
  >
    <template #actions>
      <input
        ref="fileInput"
        type="file"
        class="hidden"
        :accept="ACCEPT"
        data-testid="ai-document-input"
        @change="onFileSelected"
      />
      <div class="flex flex-col items-end gap-1">
        <Button
          :label="
            isUploading ? t('AI.DOCUMENTS.UPLOADING') : t('AI.DOCUMENTS.UPLOAD')
          "
          icon="i-lucide-upload"
          size="sm"
          :is-loading="isUploading"
          @click="openFilePicker"
        />
        <span class="text-label-small text-n-slate-10">
          {{ t('AI.DOCUMENTS.ACCEPTED') }}
        </span>
      </div>
    </template>

    <div v-if="isLoading" class="flex justify-center py-16">
      <Spinner />
    </div>

    <div
      v-else-if="!documents.length"
      class="flex flex-col items-center gap-2 px-6 py-16 text-center rounded-xl outline-1 outline-dashed outline-n-weak"
    >
      <span class="i-lucide-file-text size-8 text-n-slate-9" />
      <h2 class="text-heading-2 text-n-slate-12">
        {{ t('AI.DOCUMENTS.EMPTY_TITLE') }}
      </h2>
      <p class="text-body-para text-n-slate-11 max-w-md">
        {{ t('AI.DOCUMENTS.EMPTY_DESCRIPTION') }}
      </p>
    </div>

    <ul v-else class="flex flex-col gap-3" data-testid="ai-documents-list">
      <li
        v-for="document in documents"
        :key="document.id"
        class="flex items-center gap-4 px-5 py-4 rounded-xl bg-n-solid-1 outline outline-1 outline-n-weak"
      >
        <span class="i-lucide-file-text size-5 text-n-slate-10 shrink-0" />
        <div class="flex flex-col flex-1 min-w-0 gap-1">
          <div class="flex flex-wrap items-center gap-2">
            <span class="text-heading-3 text-n-slate-12 truncate">
              {{ document.title }}
            </span>
            <span
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-label-small"
              :class="statusClasses[document.status]"
            >
              <span
                v-if="document.status === 'processing'"
                class="i-lucide-loader-circle size-3 animate-spin"
              />
              {{ statusLabels[document.status] }}
            </span>
          </div>
          <span class="text-label-small text-n-slate-11">
            {{ documentMeta(document) }}
          </span>
          <span
            v-if="document.status === 'failed' && document.error_message"
            class="text-label-small text-n-ruby-11"
          >
            {{ document.error_message }}
          </span>
        </div>
        <div class="flex items-center gap-1 shrink-0">
          <Button
            v-if="document.status !== 'processing'"
            :label="t('AI.DOCUMENTS.REPROCESS')"
            icon="i-lucide-refresh-cw"
            variant="ghost"
            color="slate"
            size="sm"
            :is-loading="busyIds.includes(document.id)"
            @click="reprocess(document)"
          />
          <Button
            :label="t('AI.DOCUMENTS.DELETE')"
            icon="i-lucide-trash-2"
            variant="ghost"
            color="ruby"
            size="sm"
            :disabled="busyIds.includes(document.id)"
            @click="askDelete(document)"
          />
        </div>
      </li>
    </ul>

    <Dialog
      ref="deleteDialog"
      type="alert"
      :title="t('AI.DOCUMENTS.DELETE_DIALOG.TITLE')"
      :description="
        t('AI.DOCUMENTS.DELETE_DIALOG.DESCRIPTION', {
          title: documentToDelete?.title || '',
        })
      "
      :confirm-button-label="t('AI.DOCUMENTS.DELETE_DIALOG.CONFIRM')"
      :cancel-button-label="t('AI.DOCUMENTS.DELETE_DIALOG.CANCEL')"
      @confirm="confirmDelete"
    />
  </AiPageLayout>
</template>
